import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { ArrowLeftRight, X, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'Pending review', icon: Clock, cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', icon: CheckCircle2, cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', icon: XCircle, cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', icon: X, cls: 'bg-gray-100 text-gray-600' },
};

export const RequestSwapDialog = ({ open, onOpenChange, shift, onCreated }) => {
  const [reason, setReason] = useState('');
  const [recipient, setRecipient] = useState('none');
  const [coworkers, setCoworkers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setRecipient('none');
    axios.get(`${API_URL}/api/employee/coworkers`, { withCredentials: true })
      .then((r) => setCoworkers(r.data || []))
      .catch(() => setCoworkers([])); // optional endpoint
  }, [open]);

  const submit = async () => {
    if (!shift?.shift_id) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/employee/shift-swaps`, {
        shift_id: shift.shift_id,
        reason: reason.trim(),
        proposed_recipient_id: recipient && recipient !== 'none' ? recipient : null,
      }, { withCredentials: true });
      toast.success('Swap request sent — your manager will review it shortly');
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send swap request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="swap-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-[#FF3B6B]" />
            Request a Shift Swap
          </DialogTitle>
          <DialogDescription>
            Your manager will review and either approve (reassigning the shift) or reject the request. You can cancel a pending request anytime.
          </DialogDescription>
        </DialogHeader>
        {shift && (
          <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] px-3 py-2 text-sm">
            <p className="font-semibold text-[#1E1E24]">{shift.date} · {shift.start_time}–{shift.end_time}</p>
            <p className="text-xs text-[#6B6B7A] capitalize">{(shift.role || 'staff').replace(/_/g, ' ')}</p>
          </div>
        )}
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Doctor's appointment, family emergency, school event…"
              rows={3}
              className="mt-1"
              data-testid="swap-reason"
            />
          </div>
          {coworkers.length > 0 && (
            <div>
              <Label className="text-xs">Propose a specific coworker (optional)</Label>
              <Select value={recipient} onValueChange={setRecipient}>
                <SelectTrigger className="bg-white mt-1" data-testid="swap-recipient">
                  <SelectValue placeholder="Anyone available" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Anyone available (manager decides)</SelectItem>
                  {coworkers.map((c) => (
                    <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="swap-submit">
            {submitting ? 'Sending…' : 'Send Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MyShiftSwapsList = ({ refreshTrigger }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSwaps = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/employee/shift-swaps`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      // non-fatal
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSwaps(); }, [fetchSwaps, refreshTrigger]);

  const cancel = async (swap_id) => {
    if (!window.confirm('Cancel this swap request?')) return;
    try {
      await axios.delete(`${API_URL}/api/employee/shift-swaps/${swap_id}`, { withCredentials: true });
      toast.success('Swap cancelled');
      fetchSwaps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  if (loading) return <p className="text-xs text-[#6B6B7A]">Loading…</p>;
  if (items.length === 0) return <p className="px-4 py-6 text-center text-sm text-[#6B6B7A]">No swap requests yet. Tap "Request swap" on any upcoming shift.</p>;

  return (
    <div className="space-y-2" data-testid="my-swaps-list">
      {items.map((s) => {
        const meta = STATUS_LABEL[s.status] || STATUS_LABEL.pending;
        const Icon = meta.icon;
        return (
          <div key={s.swap_id}
               className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E8E5DF] bg-white px-3 py-2"
               data-testid={`swap-row-${s.swap_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#1E1E24]">
                {s.shift_date} · {s.shift_start}–{s.shift_end}
                {s.shift_role && <span className="text-xs text-[#6B6B7A] ml-2 capitalize">({s.shift_role.replace(/_/g, ' ')})</span>}
              </p>
              {s.reason && <p className="text-xs text-[#6B6B7A] mt-0.5 truncate">{s.reason}</p>}
              {s.manager_note && (
                <p className="text-[11px] text-[#6B6B7A] mt-0.5 italic">Manager note: {s.manager_note}</p>
              )}
              {s.approved_recipient_name && (
                <p className="text-[11px] text-green-700 mt-0.5">Reassigned to {s.approved_recipient_name}</p>
              )}
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1 ${meta.cls}`}>
              <Icon className="w-3 h-3" /> {meta.label}
            </span>
            {s.status === 'pending' && (
              <button
                type="button"
                onClick={() => cancel(s.swap_id)}
                className="text-xs text-[#FF3B6B] hover:underline"
                data-testid={`swap-cancel-${s.swap_id}`}
              >
                Cancel
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RequestSwapDialog;
